import { unwrapResult } from '@reduxjs/toolkit';
import { ProductionItemDetail } from 'constants/interface';
import { InventoryTypeDisplayMapping, InventoryTypeEnum } from 'constants/inventory';
import { ProductionItemSeedStatusDisplayMapping, ProductionItemSeedStatusEnum, SeedingBatchTypeEnum } from 'constants/order';
import { ROUTES } from 'constants/routes';
import { buildProductionItemQuantityDisplay } from 'helpers/order';
import { formatDate, parseUtcStringDate } from 'helpers/utils';
import { useBreadcrumbNavigation } from 'hooks/breadcrumb';
import { useDispatch } from 'hooks/store';
import { createAlertNotification } from 'infra/notification';
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { getProductionItemAction, getProductionItemSeedStatusAction } from 'state/slices/productionItem';
import {
  Descriptions,
  Tag,
} from 'UI';
import { SchedulingModal, SeedReservationModal, SeedTicketModal } from './components';

import {
  StepDescriptions,
  TitleWrapper,
  ExtraButton,
  SeedStatusSkeleton,
} from './style';

interface SeedingProgressProps {
  item: ProductionItemDetail
  // onDataUpdate: () => void
}

const SeedingProgress = ({
  item,
  // onDataUpdate,
}: SeedingProgressProps): JSX.Element => {
  const dispatch = useDispatch();
  const breadcrumbNavigator = useBreadcrumbNavigation();
  const [seedStatus, setSeedStatus] = useState<ProductionItemSeedStatusEnum>();
  const [isLoadingSeedStatus, setLoadingSeedStatus] = useState<boolean>(false);
  const [isSchedulingOpened, setSchedulingOpened] = useState(false);
  const [isSeedReservationOpened, setSeedReservationOpened] = useState(false);
  const [isSeedTicketOpened, setSeedTicketOpened] = useState(false);

  const onSchedulingClick = () => {
    setSchedulingOpened(true);
  };
  const onSchedulingModalClose = () => {
    setSchedulingOpened(false);
  };

  const onSeedReservationClick = () => {
    setSeedReservationOpened(true);
  };
  const onSeedReservationModalClose = () => {
    setSeedReservationOpened(false);
  };

  const onSeedTicketClick = () => {
    setSeedTicketOpened(true);
  };
  const onSeedTicketClose = () => {
    setSeedTicketOpened(false);
  };

  const onDataUpdate = () => {
    dispatch(getProductionItemAction({
      productionItemId: item.id,
    }));
  };

  const mainSeedingBatch = item.seedingBatches
    ? item.seedingBatches.find((seedingBatch) => seedingBatch.type === SeedingBatchTypeEnum.MAIN)
    : undefined;

  const formattedSeedingDate = useMemo(() => {
    if (!item.seedingBatches) {
      return '-';
    }
    const date = mainSeedingBatch?.seedingDate
      ? parseUtcStringDate(mainSeedingBatch.seedingDate)
      : undefined;

    if (!date) {
      return '-';
    }

    return formatDate(date);
  }, [item]);

  const onScheduleUpdated = () => {
    const successAlert = createAlertNotification('Programação salva com sucesso', AlertNotificationType.Success);
    dispatch(setNotificationAction(successAlert));
    setSchedulingOpened(false);
    onDataUpdate();
  };

  const onReservationUpdated = () => {
    const successAlert = createAlertNotification('Reserva salva com sucesso', AlertNotificationType.Success);
    dispatch(setNotificationAction(successAlert));
    setSeedReservationOpened(false);
    onDataUpdate();
  };

  const onSeedTicketCreated = () => {
    const successAlert = createAlertNotification('Solicitação salva com sucesso', AlertNotificationType.Success);
    dispatch(setNotificationAction(successAlert));
    setSeedTicketOpened(false);
    onDataUpdate();
  };

  const productionItemId = item.id;

  useEffect(() => {
    if (item.id) {
      (async (itemId) => {
        setLoadingSeedStatus(true);
        const result = await dispatch(getProductionItemSeedStatusAction({
          productionItemId: itemId,
        }));

        setLoadingSeedStatus(false);
        const status = unwrapResult(result);

        setSeedStatus(status);
      })(productionItemId);
    }
  }, [productionItemId]);

  const isPendingSchedule = !mainSeedingBatch?.seedingDate;

  const SeedActionButtonMapper: Record<
  ProductionItemSeedStatusEnum,
  (inventoryType: InventoryTypeEnum) => React.ReactNode
  > = {
    [ProductionItemSeedStatusEnum.UNAVAILABLE]: (
      inventoryType,
    ) => (inventoryType === InventoryTypeEnum.ORGANIZATION ? (
      <ExtraButton
        onClick={onSeedTicketClick}
      >
        Solicitar
      </ExtraButton>
    ) : (
      <ExtraButton
        disabled
      >
        Reservar
      </ExtraButton>
    )),
    [ProductionItemSeedStatusEnum.WAITING]: () => (
      <ExtraButton
        onClick={() => {
          // export interface BreadcrumbItem {
          //   label: string;
          //   url: string;
          // }
          // /Painel/Estoques/Viveiro/47d9fec0-d9bc-11eb-8052-5da2396bc60a/Sementes
          // Painel/Estoque/Viveiro/Unidade 1/Sementes
          const { length: range } = breadcrumbNavigator.current();
          breadcrumbNavigator.replace([
            {
              label: ROUTES.dashboard.breadcrumb,
              url: ROUTES.dashboard.root,
            },
            {
              label: ROUTES.dashboard.inventory.breadcrumb,
              url: `\
${ROUTES.dashboard.root}\
${ROUTES.dashboard.inventory.root}\
`,
            },
            {
              label: ROUTES.dashboard.inventory.organization.breadcrumb,
              url: `\
${ROUTES.dashboard.root}\
${ROUTES.dashboard.inventory.root}\
${ROUTES.dashboard.inventory.organization.root}\
`,
            },
            {
              label: ROUTES.dashboard.inventory.organization.selected.breadcrumb(
                item.orderItem.order.organization.name,
              ),
              url: `\
${ROUTES.dashboard.root}\
${ROUTES.dashboard.inventory.root}\
${ROUTES.dashboard.inventory.organization.root}\
/${item.orderItem.order.organization.id}\
`,
            },
            {
              label: ROUTES.dashboard.inventory.breadcrumb,
              url: `\
${ROUTES.dashboard.root}\
${ROUTES.dashboard.inventory.root}\
${ROUTES.dashboard.inventory.organization.root}\
/${item.orderItem.order.organization.id}\
${ROUTES.dashboard.inventory.organization.selected.seed.root}\
`,
            },
          ], range);
        }}
      >
        Receber
      </ExtraButton>
    ),
    [ProductionItemSeedStatusEnum.AVAILABLE]: () => (
      <ExtraButton
        onClick={onSeedReservationClick}
      >
        Reservar
      </ExtraButton>
    ),
    [ProductionItemSeedStatusEnum.OK]: () => (
      <ExtraButton
        onClick={onSeedReservationClick}
      >
        Editar
      </ExtraButton>
    ),
  };

  return (
    <>
      <StepDescriptions
        bordered
        layout="horizontal"
        title={(
          <TitleWrapper>
            Programação
            {
              isPendingSchedule
                ? (<Tag color="warning">Pendente</Tag>)
                : null
            }
          </TitleWrapper>
        )}
        extra={(
          <TitleWrapper>
            <ExtraButton onClick={onSchedulingClick}>
              {isPendingSchedule ? 'Programar' : 'Editar'}
            </ExtraButton>
          </TitleWrapper>
        )}
      >
        <Descriptions.Item
          label="Data"
        >
          {formattedSeedingDate}
        </Descriptions.Item>
        <Descriptions.Item
          label="Quantidade"
        >
          {`${buildProductionItemQuantityDisplay(item)}`}
        </Descriptions.Item>
        <Descriptions.Item
          label="Estufa"
        >
          {mainSeedingBatch?.greenhouse?.name || '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label="Observações"
        >
          {mainSeedingBatch?.notes || '-'}
        </Descriptions.Item>
      </StepDescriptions>

      <StepDescriptions
        bordered
        layout="horizontal"
        title={(
          <TitleWrapper>
            Sementes
            {isLoadingSeedStatus === false && seedStatus !== ProductionItemSeedStatusEnum.OK && (
              <Tag color="warning">Pendente</Tag>
            )}
          </TitleWrapper>
        )}
        extra={(
          <TitleWrapper>
            {/* <ExtraButton>
              Reservar
            </ExtraButton> */}
            {seedStatus && SeedActionButtonMapper[seedStatus](item.seedSource)}
          </TitleWrapper>
        )}
      >
        <Descriptions.Item
          label="Semente"
        >
          {InventoryTypeDisplayMapping[item.seedSource].name}
        </Descriptions.Item>
        <Descriptions.Item
          label="Status"
        >
          {
            isLoadingSeedStatus || !seedStatus ? (
              <SeedStatusSkeleton
                loading
                paragraph={{
                  rows: 1,
                }}
                title={false}
              />
            ) : (
              <Tag
                color={ProductionItemSeedStatusDisplayMapping[seedStatus].color}
              >
                {ProductionItemSeedStatusDisplayMapping[seedStatus].name}
              </Tag>
            )
          }
        </Descriptions.Item>
      </StepDescriptions>

      <SchedulingModal
        item={item}
        isVisible={isSchedulingOpened}
        onCancel={onSchedulingModalClose}
        onSuccess={onScheduleUpdated}
      />

      <SeedReservationModal
        item={item}
        isVisible={isSeedReservationOpened}
        onCancel={onSeedReservationModalClose}
        onSuccess={onReservationUpdated}
      />

      <SeedTicketModal
        item={item}
        isVisible={isSeedTicketOpened}
        onCancel={onSeedTicketClose}
        onSuccess={onSeedTicketCreated}
      />
    </>
  );
};

export default SeedingProgress;
